/**
 *  @swagger
 *  definitions:
 *    BannerLocationParams:
 *      type: object
 *      properties:
 *        BannerId:
 *          description: ID of the associated Banner
 *          type: string
 *        RegionId:
 *          description: ID of the associated Region
 *          type: string
 *        Type:
 *          description: Type of the BannerLocation
 *          $ref: '#/components/enums/BannerLocationType'
 *        ReferenceId:
 *          description: ReferenceId of the BannerLocation
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    BannerLocationCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/BannerLocationParams'
 *        - type: object
 *          required:
 *            - BannerId
 *            - RegionId
 *            - Type
 *
 *  @swagger
 *  definitions:
 *    BannerLocationQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the BannerLocation
 *              type: string
 *        - $ref: '#/definitions/BannerLocationParams'
 *
 *  @swagger
 *  definitions:
 *    BannerLocationUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/BannerLocationQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    BannerLocation:
 *      allOf:
 *        - $ref: '#/definitions/BannerLocationCreateParams'
 *        - $ref: '#/definitions/BannerLocationUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      BannerLocation:
 *        allOf:
 *          - $ref: '#/definitions/BannerLocation'
 *          - type: object
 *            properties:
 *              Banner:
 *                description: Banner of the BannerLocation
 *                $ref: '#/components/schemas/Banner'
 *              Region:
 *                description: Region of the BannerLocation
 *                $ref: '#/components/schemas/Region'
 */

// Enums
import {
  BannerLocationType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  BannerId: string;
  RegionId: string;
};

export type $Params = {
  ReferenceId: null | string;
  Type: BannerLocationType;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Banner?: $Model<'Banner'>;
  Region?: $Model<'Region'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  ReferenceId: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(BannerLocationType)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'BannerLocation'> => {
  const BannerLocation = stack.define(
    'BannerLocation',
    fields(Sequelize),
    {
      tableName: 'BannerLocations',
    },
  );

  BannerLocation.associate = (models: $ModelClasses) => {
    models.BannerLocation.belongsTo(
      models.Region,
      {
        as: 'Region',
        foreignKey: 'RegionId',
      },
    );
    models.BannerLocation.belongsTo(
      models.Banner,
      {
        as: 'Banner',
        foreignKey: 'BannerId',
      },
    );
  };

  BannerLocation.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return BannerLocation;
});
